import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type ReactFlowInstance
} from "reactflow";
import "reactflow/dist/style.css";
import { FiAlignJustify, FiDownload, FiGrid, FiPlay, FiPlus } from "react-icons/fi";
import type {
  DataMartDimension,
  DataMartFact,
  DataMartGraph,
  DataMartModel,
  DataMartRelationship,
  SelectedDataMartEntity
} from "./model/types";
import { buildDataMartColumn } from "./model/buildModel";
import { FactNode } from "./components/FactNode";
import { DimensionNode } from "./components/DimensionNode";
import { DataMartEntityPanel } from "./components/DataMartEntityPanel";
import {
  GenerationReportSummary,
  type GenerationReport
} from "../../components/modelling/GenerationReportSummary";
import type { DataMartNodeData } from "./components/types";
import { ToolbarButton } from "../mapping/components/ToolbarButton";
import { buildFlowElements, computeLayout, type DataMartEdgeData } from "./workspace/flow";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { hasCoordinateChanged } from "../../helpers/canvasPosition";
import { SHARED_CANVAS_INTERACTION_PROPS } from "../mapping/shared/canvasInteraction";
import { useStatusToast } from "../../hooks/useStatusToast";
import type {
  IDataMartRecord,
  ITargetEntityDmColumnRecord,
  ITargetEntityDmRecord,
  ITargetEntityDmRelationshipRecord
} from "../../interfaces/dataMartModelling.interfaces";
import {
  loadDataMartModel,
  fetchTargetEntityRelationships,
  createTargetEntityRelationships,
  deleteTargetEntityRelationship,
  deleteTargetEntityRelationshipById,
  deleteTargetEntityDm,
  fetchRelatedRdvEntities,
  updateDataMartLayout,
  promoteInternalGoldAssetToDataMart,
  generateDataMartSqlPreview,
  generateDqTestDataMartSqlPreview,
  type DataMartSqlPreviewResponse,
  type TargetEntityRdvRecord
} from "../../services/dataMartModelling.service";
import { createDataMart, getDataMarts, getDataSources } from "../../services/dataLinage.service";
import { matchesCanvasFilter } from "../../helpers/canvasFilter";
import { downloadFile } from "../mapping/model/files";
import { SqlPreview } from "../mapping/components/forms/SqlPreview";
import {
  DIMENSION_TABLE_CONSTRUCT_OPTIONS,
  DIMENSION_TABLE_CONSTRUCT_DEFAULT,
  FACT_TABLE_CONSTRUCT_DEFAULT
} from "./constants";
import { getScopeCustomerId, getScopeEnvList, getScopeEnvironmentId } from "../../services/activeScope";

const nodeTypes = {
  factNode: FactNode,
  dimensionNode: DimensionNode
};

const padSequence = (value: number) => value.toString().padStart(2, "0");

const toScopeNumber = (value: string | null): number => {
  if (!value) {
    return 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getEnvironmentId = () => toScopeNumber(getScopeEnvironmentId());
const getCustomerId = () => toScopeNumber(getScopeCustomerId());
const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (error && typeof error === "object") {
    const maybeError = error as {
      response?: { data?: { detail?: unknown } };
      resBody?: { detail?: unknown };
      body?: { detail?: unknown };
    };
    const detail =
      maybeError.response?.data?.detail ??
      maybeError.resBody?.detail ??
      maybeError.body?.detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail)) {
      return detail
        .map((item) => (typeof item === "string" ? item : JSON.stringify(item)))
        .join(", ");
    }
  }
  return fallback;
};
const getActiveEnvironmentDwTargetType = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const envListRaw = getScopeEnvList();
    const environmentIdRaw = getScopeEnvironmentId();
    if (!envListRaw || !environmentIdRaw) {
      return null;
    }
    const parsed = JSON.parse(envListRaw);
    const environmentId = Number(environmentIdRaw);
    if (!Array.isArray(parsed) || Number.isNaN(environmentId)) {
      return null;
    }
    const activeEnvironment = parsed.find((env) => {
      if (!env) {
        return false;
      }
      const candidateId =
        typeof env.environment_id === "number"
          ? env.environment_id
          : Number(env.environment_id);
      return Number.isFinite(candidateId) && candidateId === environmentId;
    });
    // On-prem target takes precedence when set (Aleris uses dw_target_types_onprem =
    // LAKEHOUSE); mirrors the backend resolver in app/api/automation.py.
    const onpremTargetType = activeEnvironment?.dw_target_types_onprem;
    if (typeof onpremTargetType === "string" && onpremTargetType.trim()) {
      return onpremTargetType.trim();
    }
    const targetType = activeEnvironment?.dw_target_types;
    if (typeof targetType === "string" && targetType.trim()) {
      return targetType.trim();
    }
  } catch (error) {
    console.warn("Unable to resolve environment dw_target_types", error);
  }
  return null;
};

const isLakehouseTargetType = (targetType: string | null): boolean =>
  (targetType ?? "").trim().toUpperCase() === "LAKEHOUSE";
type FactDimensionHandles = {
  sourceHandle: string | null;
  targetHandle: string | null;
};

// `subject_area` was offered here and could never match: dwa.target_entity_dm has no such column,
// so `record.subject_area` was always null. Replaced by target_schema, which the table does have
// and which is what actually separates one set of marts from another.
type DataMartFilterField = "" | "target_entity_dm_name" | "datamart" | "target_schema";

type ActiveDataMartFilter = {
  field: Exclude<DataMartFilterField, "">;
  value: string;
};

type DataMartOption = {
  data_mart_id: number;
  data_mart: string;
};

type AttachableGoldAssetOption = {
  data_source_id: number;
  data_source_name: string;
  source_entity_id: number;
  source_entity_name: string;
  source_entity_description?: string | null;
  source_schema?: string | null;
  source_entity_kind?: string | null;
  source_entity_origin?: string | null;
};

type DataSourceEntityRecord = {
  source_entity_id: number;
  source_entity_name: string;
  source_entity_description?: string | null;
  source_schema?: string | null;
  source_entity_layer?: string | null;
  source_entity_kind?: string | null;
  source_entity_origin?: string | null;
};

type DataSourceListRecord = {
  data_source_id: number;
  data_source_name: string;
  data_source_type?: string | null;
  source_entity?: DataSourceEntityRecord[] | null;
};

const INTERNAL_REGISTERED_DATA_SOURCE_TYPE = "INTERNAL_REGISTERED";
const CREATED_NODE_FOCUS_WIDTH = 240;
const CREATED_NODE_FOCUS_HEIGHT = 160;
const CREATED_NODE_FOCUS_ZOOM = 1.2;
const CREATED_NODE_FOCUS_RETRIES = 12;
const NEW_ENTITY_CLUSTER_GAP_X = CREATED_NODE_FOCUS_WIDTH + 90;
const NEW_ENTITY_CLUSTER_GAP_Y = CREATED_NODE_FOCUS_HEIGHT + 80;
const NEW_ENTITY_CLUSTER_OFFSETS = [
  { x: 0, y: 0 },
  { x: NEW_ENTITY_CLUSTER_GAP_X, y: 0 },
  { x: 0, y: NEW_ENTITY_CLUSTER_GAP_Y },
  { x: NEW_ENTITY_CLUSTER_GAP_X, y: NEW_ENTITY_CLUSTER_GAP_Y },
  { x: -NEW_ENTITY_CLUSTER_GAP_X, y: 0 },
  { x: -NEW_ENTITY_CLUSTER_GAP_X, y: NEW_ENTITY_CLUSTER_GAP_Y },
  { x: 0, y: -NEW_ENTITY_CLUSTER_GAP_Y },
  { x: NEW_ENTITY_CLUSTER_GAP_X, y: -NEW_ENTITY_CLUSTER_GAP_Y }
];

const createEmptyGraph = (name = "DATA_MART_01"): DataMartGraph => {
  const dataMart: IDataMartRecord = {
    data_mart_id: null,
    data_mart: name,
    environment_id: getEnvironmentId(),
    customer_id: getCustomerId()
  };
  return {
    dataMart,
    facts: [],
    dimensions: [],
    relationships: []
  };
};

const createBlankModel = (): DataMartModel => ({
  graphs: [createEmptyGraph()]
});

const isInternalRegisteredSource = (value?: string | null) =>
  (value ?? "").trim().toUpperCase() === INTERNAL_REGISTERED_DATA_SOURCE_TYPE;

const isGoldLayerEntity = (value?: string | null) => (value ?? "").trim().toLowerCase() === "gold";

const isModeledDataMartNodeData = (
  data: DataMartNodeData
): data is DataMartNodeData & { kind: "fact" | "dimension"; entity: DataMartFact | DataMartDimension } =>
  data.kind === "fact" || data.kind === "dimension";

const buildRelationshipId = (
  factEntityId: string,
  dimensionEntityId: string,
  record?: ITargetEntityDmRelationshipRecord | null
) =>
  record?.target_entity_dm_relationship_id
    ? `dm-rel-${record.target_entity_dm_relationship_id}`
    : `${factEntityId}->${dimensionEntityId}`;

const toSourceHandleId = (handleId: string | null | undefined) => {
  if (!handleId) {
    return null;
  }
  return handleId.endsWith("-target") ? `${handleId.slice(0, -"-target".length)}-source` : handleId;
};

const toTargetHandleId = (handleId: string | null | undefined) => {
  if (!handleId) {
    return null;
  }
  return handleId.endsWith("-source") ? `${handleId.slice(0, -"-source".length)}-target` : handleId;
};

const sanitizeHandleId = (handleId: string | null | undefined) => {
  if (!handleId) {
    return null;
  }
  return handleId.trim().toLowerCase().replace(/_/g, "-");
};

const normalizeFactDimensionHandles = (
  connection: Connection,
  direction: "fact-to-dimension" | "dimension-to-fact"
) => {
  if (direction === "fact-to-dimension") {
    return {
      sourceHandle: toSourceHandleId(connection.sourceHandle),
      targetHandle: toTargetHandleId(connection.targetHandle)
    };
  }
  return {
    sourceHandle: toSourceHandleId(connection.targetHandle),
    targetHandle: toTargetHandleId(connection.sourceHandle)
  };
};

const handlesFromRelationshipRecord = (
  record?: ITargetEntityDmRelationshipRecord | null
): FactDimensionHandles | undefined => {
  if (!record) {
    return undefined;
  }
  return {
    sourceHandle: toSourceHandleId(sanitizeHandleId(record.source_handle)),
    targetHandle: toTargetHandleId(sanitizeHandleId(record.target_handle))
  };
};

const addRelationshipToModel = (
  model: DataMartModel | null,
  factEntityId: string,
  dimensionEntityId: string,
  record?: ITargetEntityDmRelationshipRecord | null,
  handles?: { sourceHandle: string | null; targetHandle: string | null }
): DataMartModel | null => {
  if (!model) {
    return model;
  }
  let hasUpdated = false;
  const graphs = model.graphs.map((graph) => {
    const factIndex = graph.facts.findIndex((fact) => fact.id === factEntityId);
    const dimensionIndex = graph.dimensions.findIndex((dimension) => dimension.id === dimensionEntityId);
    if (factIndex === -1 || dimensionIndex === -1) {
      return graph;
    }
    const fact = graph.facts[factIndex];
    const dimension = graph.dimensions[dimensionIndex];
    const existingIndex = graph.relationships.findIndex(
      (relation) =>
        relation.type === "fact-dimension" &&
        relation.sourceId === fact.id &&
        relation.targetId === dimension.id
    );
    const nextRelationships =
      existingIndex !== -1
        ? graph.relationships.map((relation, index) =>
            index === existingIndex
              ? {
                  ...relation,
                  sourceHandle: handles ? handles.sourceHandle : relation.sourceHandle,
                  targetHandle: handles ? handles.targetHandle : relation.targetHandle,
                  record: record ?? relation.record ?? null
                }
              : relation
          )
        : [
            ...graph.relationships,
            {
              id: buildRelationshipId(fact.id, dimension.id, record),
              type: "fact-dimension" as const,
              sourceId: fact.id,
              targetId: dimension.id,
              sourceHandle: handles?.sourceHandle ?? null,
              targetHandle: handles?.targetHandle ?? null,
              columnNames: [] as string[],
              record: record ?? null
            }
          ];
    const dimensionIds = fact.dimensionIds.includes(dimension.id)
      ? fact.dimensionIds
      : [...fact.dimensionIds, dimension.id];
    const nextFact = fact.dimensionIds === dimensionIds ? fact : { ...fact, dimensionIds };
    const nextFacts = [...graph.facts];
    nextFacts[factIndex] = nextFact;
    hasUpdated = true;
    return {
      ...graph,
      facts: nextFacts,
      relationships: nextRelationships
    };
  });
  if (!hasUpdated) {
    return model;
  }
  return { ...model, graphs };
};

const removeRelationshipFromModel = (
  model: DataMartModel | null,
  factEntityId: string,
  dimensionEntityId: string
): DataMartModel | null => {
  if (!model) {
    return model;
  }
  let hasUpdated = false;
  const graphs = model.graphs.map((graph) => {
    const factIndex = graph.facts.findIndex((fact) => fact.id === factEntityId);
    const dimensionIndex = graph.dimensions.findIndex((dimension) => dimension.id === dimensionEntityId);
    if (factIndex === -1 || dimensionIndex === -1) {
      return graph;
    }
    const nextRelationships = graph.relationships.filter(
      (relation) =>
        relation.type !== "fact-dimension" ||
        relation.sourceId !== factEntityId ||
        relation.targetId !== dimensionEntityId
    );
    const nextFacts = [...graph.facts];
    if (graph.facts[factIndex].dimensionIds.includes(dimensionEntityId)) {
      const filteredDimensionIds = graph.facts[factIndex].dimensionIds.filter((id) => id !== dimensionEntityId);
      nextFacts[factIndex] = { ...graph.facts[factIndex], dimensionIds: filteredDimensionIds };
    }
    hasUpdated = true;
    return {
      ...graph,
      facts: nextFacts,
      relationships: nextRelationships
    };
  });
  if (!hasUpdated) {
    return model;
  }
  return { ...model, graphs };
};

const removeDataMartEntityFromModel = (
  model: DataMartModel | null,
  entityId: string,
  entityType: "fact" | "dimension"
): DataMartModel | null => {
  if (!model) {
    return model;
  }
  let hasUpdated = false;
  const graphs = model.graphs.map((graph) => {
    if (entityType === "fact") {
      const exists = graph.facts.some((fact) => fact.id === entityId);
      if (!exists) {
        return graph;
      }
      hasUpdated = true;
      return {
        ...graph,
        facts: graph.facts.filter((fact) => fact.id !== entityId),
        relationships: graph.relationships.filter(
          (relation) =>
            !(
              relation.type === "fact-dimension" &&
              relation.sourceId === entityId
            )
        )
      };
    }

    const exists = graph.dimensions.some((dimension) => dimension.id === entityId);
    if (!exists) {
      return graph;
    }
    hasUpdated = true;
    const nextDimensions = graph.dimensions
      .filter((dimension) => dimension.id !== entityId)
      .map((dimension) => ({
        ...dimension,
        parentDimensionIds: dimension.parentDimensionIds.filter((id) => id !== entityId),
        childDimensionIds: dimension.childDimensionIds.filter((id) => id !== entityId)
      }));
    const nextFacts = graph.facts.map((fact) => ({
      ...fact,
      dimensionIds: fact.dimensionIds.filter((id) => id !== entityId)
    }));
    const nextRelationships = graph.relationships.filter((relation) => {
      if (relation.type === "fact-dimension") {
        return relation.targetId !== entityId;
      }
      if (relation.type === "dimension-dimension") {
        return relation.sourceId !== entityId && relation.targetId !== entityId;
      }
      return true;
    });
    return {
      ...graph,
      facts: nextFacts,
      dimensions: nextDimensions,
      relationships: nextRelationships
    };
  });
  if (!hasUpdated) {
    return model;
  }
  return { ...model, graphs };
};

const applyServerRelationships = (
  model: DataMartModel | null,
  relationships: ITargetEntityDmRelationshipRecord[]
): DataMartModel | null => {
  if (!model || !relationships.length) {
    return model;
  }
  const relationshipByPair = new Map<string, ITargetEntityDmRelationshipRecord>();
  relationships.forEach((relationship) => {
    const pairKey = `${relationship.fact_target_entity_dm_id}->${relationship.dimension_target_entity_dm_id}`;
    const existing = relationshipByPair.get(pairKey);
    if (!existing) {
      relationshipByPair.set(pairKey, relationship);
      return;
    }

    const existingUpdatedAt = existing.updated_at ? Date.parse(existing.updated_at) : 0;
    const nextUpdatedAt = relationship.updated_at ? Date.parse(relationship.updated_at) : 0;
    const existingCreatedAt = existing.created_at ? Date.parse(existing.created_at) : 0;
    const nextCreatedAt = relationship.created_at ? Date.parse(relationship.created_at) : 0;

    const shouldReplace =
      nextUpdatedAt > existingUpdatedAt ||
      (nextUpdatedAt === existingUpdatedAt && nextCreatedAt > existingCreatedAt) ||
      (nextUpdatedAt === existingUpdatedAt &&
        nextCreatedAt === existingCreatedAt &&
        relationship.target_entity_dm_relationship_id > existing.target_entity_dm_relationship_id);

    if (shouldReplace) {
      relationshipByPair.set(pairKey, {
        ...relationship,
        source_handle: relationship.source_handle ?? existing.source_handle ?? null,
        target_handle: relationship.target_handle ?? existing.target_handle ?? null
      });
      return;
    }

    relationshipByPair.set(pairKey, {
      ...existing,
      source_handle: existing.source_handle ?? relationship.source_handle ?? null,
      target_handle: existing.target_handle ?? relationship.target_handle ?? null
    });
  });

  const lookup = new Map<
    number,
    {
      graphIndex: number;
      entityId: string;
    }
  >();
  model.graphs.forEach((graph, graphIndex) => {
    graph.facts.forEach((fact) => {
      if (fact.record.target_entity_dm_id != null) {
        lookup.set(fact.record.target_entity_dm_id, { graphIndex, entityId: fact.id });
      }
    });
    graph.dimensions.forEach((dimension) => {
      if (dimension.record.target_entity_dm_id != null) {
        lookup.set(dimension.record.target_entity_dm_id, {
          graphIndex,
          entityId: dimension.id
        });
      }
    });
  });
  let nextModel: DataMartModel | null = model;
  relationshipByPair.forEach((relationship) => {
    const factEntry = lookup.get(relationship.fact_target_entity_dm_id);
    const dimensionEntry = lookup.get(relationship.dimension_target_entity_dm_id);
    if (!factEntry || !dimensionEntry) {
      return;
    }
    if (factEntry.graphIndex !== dimensionEntry.graphIndex) {
      return;
    }
    nextModel = addRelationshipToModel(
      nextModel,
      factEntry.entityId,
      dimensionEntry.entityId,
      relationship,
      handlesFromRelationshipRecord(relationship)
    );
  });
  return nextModel;
};

const findRelationship = (
  model: DataMartModel | null,
  factEntityId: string,
  dimensionEntityId: string
) => {
  if (!model) {
    return null;
  }
  for (const graph of model.graphs) {
    const relation = graph.relationships.find(
      (candidate) =>
        candidate.type === "fact-dimension" &&
        candidate.sourceId === factEntityId &&
        candidate.targetId === dimensionEntityId
    );
    if (relation) {
      return relation;
    }
  }
  return null;
};

const resolveFactDimensionConnection = (
  model: DataMartModel | null,
  connection: Connection
): { fact: DataMartFact; dimension: DataMartDimension } | null => {
  if (!model) {
    return null;
  }
  for (const graph of model.graphs) {
    const sourceFact = graph.facts.find((fact) => fact.id === connection.source);
    const targetFact = graph.facts.find((fact) => fact.id === connection.target);
    const sourceDimension = graph.dimensions.find((dimension) => dimension.id === connection.source);
    const targetDimension = graph.dimensions.find((dimension) => dimension.id === connection.target);
    if (sourceFact && targetDimension) {
      return { fact: sourceFact, dimension: targetDimension };
    }
    if (targetFact && sourceDimension) {
      return { fact: targetFact, dimension: sourceDimension };
    }
  }
  return null;
};

const findEntitiesByIds = (
  model: DataMartModel | null,
  factEntityId: string,
  dimensionEntityId: string
): { fact: DataMartFact; dimension: DataMartDimension } | null => {
  if (!model) {
    return null;
  }
  for (const graph of model.graphs) {
    const fact = graph.facts.find((candidate) => candidate.id === factEntityId);
    if (!fact) {
      continue;
    }
    const dimension = graph.dimensions.find((candidate) => candidate.id === dimensionEntityId);
    if (!dimension) {
      continue;
    }
    return { fact, dimension };
  }
  return null;
};

const buildEntityRecord = (
  graph: DataMartGraph,
  kind: "fact" | "dimension",
  sequence: number
): ITargetEntityDmRecord => {
  const namePrefix = kind === "fact" ? "F_NEW_FACT" : "D_NEW_DIM";
  const entityName = `${namePrefix}_${padSequence(sequence)}`;
  const now = new Date().toISOString();

  return {
    target_entity_dm_id: null,
    subject_area: null,
    target_entity_dm_name: entityName,
    target_entity_dm_report_view_name: entityName,
    datamart: graph.dataMart.data_mart,
    table_construct:
      kind === "fact" ? FACT_TABLE_CONSTRUCT_DEFAULT : DIMENSION_TABLE_CONSTRUCT_DEFAULT,
    distribution_type: null,
    distribution_key: null,
    database_target_type: null,
    target_entity_rdv_id: null,
    target_entity_rdv_source_filter: null,
    description: `New ${kind === "fact" ? "fact" : "dimension"} entity`,
    target_schema: "analytics",
    data_mart_id: null,
    customer_id: graph.dataMart.customer_id ?? 0,
    environment_id: graph.dataMart.environment_id ?? 0,
    generate_model_entity: true,
    include_dq_test: true,
    target_entity_dm_report_view_source_filter: null,
    dq_source_filter: null,
    created_at: now,
    updated_at: now
  };
};

const createFactEntity = (graph: DataMartGraph): DataMartFact => {
  const record = buildEntityRecord(graph, "fact", graph.facts.length + 1);
  return {
    id: `${record.target_schema ?? "analytics"}_${record.target_entity_dm_name}`,
    record,
    kind: "fact",
    columns: [],
    dimensionIds: []
  };
};

const createDimensionEntity = (graph: DataMartGraph): DataMartDimension => {
  const record = buildEntityRecord(graph, "dimension", graph.dimensions.length + 1);
  return {
    id: `${record.target_schema ?? "analytics"}_${record.target_entity_dm_name}`,
    record,
    kind: "dimension",
    columns: [],
    parentDimensionIds: [],
    childDimensionIds: []
  };
};

const linkEntities = (
  graph: DataMartGraph,
  connection: Connection
): { graph: DataMartGraph; updated: boolean } => {
  const sourceFact = graph.facts.find((fact) => fact.id === connection.source);
  const targetFact = graph.facts.find((fact) => fact.id === connection.target);
  const sourceDimension = graph.dimensions.find((dimension) => dimension.id === connection.source);
  const targetDimension = graph.dimensions.find((dimension) => dimension.id === connection.target);

  let fact: DataMartFact | null = null;
  let dimension: DataMartDimension | null = null;

  if (sourceFact && targetDimension) {
    fact = sourceFact;
    dimension = targetDimension;
  } else if (targetFact && sourceDimension) {
    fact = targetFact;
    dimension = sourceDimension;
  }

  if (fact && dimension) {
    const handles = normalizeFactDimensionHandles(
      connection,
      sourceFact && targetDimension ? "fact-to-dimension" : "dimension-to-fact"
    );
    const relationshipIndex = graph.relationships.findIndex(
      (relation) =>
        relation.type === "fact-dimension" &&
        relation.sourceId === fact?.id &&
        relation.targetId === dimension?.id
    );
    const relationshipExists = relationshipIndex !== -1;

    const factAlreadyLinked = fact.dimensionIds.includes(dimension.id);
    const existingRelationship = relationshipIndex !== -1 ? graph.relationships[relationshipIndex] : null;
    const handlesChanged =
      existingRelationship?.sourceHandle !== handles.sourceHandle ||
      existingRelationship?.targetHandle !== handles.targetHandle;

    if (relationshipExists && factAlreadyLinked && !handlesChanged) {
      return { graph, updated: false };
    }

    const updatedFacts = factAlreadyLinked
      ? graph.facts
      : graph.facts.map((candidate) =>
          candidate.id === fact?.id
            ? { ...candidate, dimensionIds: [...candidate.dimensionIds, dimension.id] }
            : candidate
        );

    const updatedRelationships: DataMartRelationship[] = relationshipExists
      ? graph.relationships.map((relation, index) =>
          index === relationshipIndex
            ? {
                ...relation,
                sourceHandle: handles.sourceHandle,
                targetHandle: handles.targetHandle
              }
            : relation
        )
      : [
          ...graph.relationships,
          {
            id: `${fact.id}->${dimension.id}-${Date.now()}`,
            type: "fact-dimension",
            sourceId: fact.id,
            targetId: dimension.id,
            sourceHandle: handles.sourceHandle,
            targetHandle: handles.targetHandle,
            columnNames: [] as string[]
          }
        ];

    return {
      graph: {
        ...graph,
        facts: updatedFacts,
        relationships: updatedRelationships
      },
      updated: true
    };
  }

  if (sourceDimension && targetDimension) {
    const parentDimension = sourceDimension;
    const childDimension = targetDimension;

    const relationshipExists = graph.relationships.some(
      (relation) =>
        relation.type === "dimension-dimension" &&
        relation.sourceId === parentDimension.id &&
        relation.targetId === childDimension.id
    );

    if (relationshipExists) {
      return { graph, updated: false };
    }

    const updatedDimensions = graph.dimensions.map((candidate) => {
      if (candidate.id === parentDimension.id) {
        return candidate.childDimensionIds.includes(childDimension.id)
          ? candidate
          : { ...candidate, childDimensionIds: [...candidate.childDimensionIds, childDimension.id] };
      }
      if (candidate.id === childDimension.id) {
        return candidate.parentDimensionIds.includes(parentDimension.id)
          ? candidate
          : { ...candidate, parentDimensionIds: [...candidate.parentDimensionIds, parentDimension.id] };
      }
      return candidate;
    });

    const updatedRelationships: DataMartRelationship[] = [
      ...graph.relationships,
      {
        id: `${parentDimension.id}->${childDimension.id}-${Date.now()}`,
        type: "dimension-dimension",
        sourceId: parentDimension.id,
        targetId: childDimension.id,
        columnNames: [] as string[]
      }
    ];

    return {
      graph: {
        ...graph,
        dimensions: updatedDimensions,
        relationships: updatedRelationships
      },
      updated: true
    };
  }

  return { graph, updated: false };
};

export function DataMartWorkspaceInner(): JSX.Element {
  const activeDwTargetType = useMemo(() => getActiveEnvironmentDwTargetType(), []);
  const isLakehouseTarget = useMemo(
    () => isLakehouseTargetType(activeDwTargetType),
    [activeDwTargetType]
  );
  const [model, setModel] = useState<DataMartModel | null>(null);
  const [selectedGraphKey, setSelectedGraphKey] = useState<string | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [entityPanelOpen, setEntityPanelOpen] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState<DataMartNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<DataMartEdgeData>([]);
  const [selectedRelationshipEdgeId, setSelectedRelationshipEdgeId] = useState<string | null>(null);
  const [deletingRelationshipEdgeId, setDeletingRelationshipEdgeId] = useState<string | null>(null);
  const [isLoadingModel, setIsLoadingModel] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attachableGoldAssets, setAttachableGoldAssets] = useState<AttachableGoldAssetOption[]>([]);
  const [isLoadingAttachableGoldAssets, setIsLoadingAttachableGoldAssets] = useState(false);
  const [promoteGoldAssetsError, setPromoteGoldAssetsError] = useState<string | null>(null);
  const [promoteGoldModalOpen, setPromoteGoldModalOpen] = useState(false);
  const [selectedPromotableGoldAssetId, setSelectedPromotableGoldAssetId] = useState("");
  const [selectedPromotionDataMartName, setSelectedPromotionDataMartName] = useState("");
  const [isAddingPromotionDataMart, setIsAddingPromotionDataMart] = useState(false);
  const [newPromotionDataMartName, setNewPromotionDataMartName] = useState("");
  const [promotionDataMartCreateError, setPromotionDataMartCreateError] = useState<string | null>(null);
  const [isCreatingPromotionDataMart, setIsCreatingPromotionDataMart] = useState(false);
  const [promoteTableConstruct, setPromoteTableConstruct] = useState<string>(
    DIMENSION_TABLE_CONSTRUCT_DEFAULT
  );
  const [isPromotingGoldAsset, setIsPromotingGoldAsset] = useState(false);
  const [rdvEntities, setRdvEntities] = useState<TargetEntityRdvRecord[]>([]);
  const [isLoadingRdvEntities, setIsLoadingRdvEntities] = useState(false);
  const [rdvEntitiesError, setRdvEntitiesError] = useState<string | null>(null);
  const [dataMartOptions, setDataMartOptions] = useState<DataMartOption[]>([]);
  const [isLoadingDataMarts, setIsLoadingDataMarts] = useState(false);
  const [dataMartsError, setDataMartsError] = useState<string | null>(null);
  const [subjectAreaSearchQuery, setSubjectAreaSearchQuery] = useState("");
  const [isFilterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterField, setFilterField] = useState<DataMartFilterField>("");
  const [filterValueDraft, setFilterValueDraft] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveDataMartFilter | null>(null);
  const { statusMessage, showStatus: setStatusMessage } = useStatusToast(2500);
  const [generationModalOpen, setGenerationModalOpen] = useState(false);
  const [generationMode, setGenerationMode] = useState<"model" | "dq">("model");
  const [generationDataMartName, setGenerationDataMartName] = useState("");
  const [generationGenerateInserts, setGenerationGenerateInserts] = useState(false);
  const [generationFileSystem, setGenerationFileSystem] = useState("");
  const [generationSqlPreview, setGenerationSqlPreview] = useState("");
  const [generationSqlError, setGenerationSqlError] = useState<string | null>(null);
  const [generationSqlWarning, setGenerationSqlWarning] = useState<string | null>(null);
  // Coverage of the last model-SQL run: how many of the data mart's tables actually came
  // back, and why the rest did not.
  const [generationReport, setGenerationReport] = useState<GenerationReport | null>(null);
  const [generationCopyStatus, setGenerationCopyStatus] = useState<"idle" | "success" | "error">("idle");
  const [isGenerationSqlLoading, setIsGenerationSqlLoading] = useState(false);
  const [isGenerationSqlStale, setIsGenerationSqlStale] = useState(false);
  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);
  const pendingCreatedNodeFocusRef = useRef<string | null>(null);
  const createdNodeClusterAnchorRef = useRef<{ x: number; y: number } | null>(null);
  const createdNodeClusterIndexRef = useRef(0);
  const lastFocusedEntitySearchKeyRef = useRef("");
  const generationSqlCopyFeedbackTimeoutRef = useRef<number | undefined>(undefined);

  const loadHydratedDataMartModel = useCallback(async () => {
    const loadedModel = await loadDataMartModel();
    const relationships = await fetchTargetEntityRelationships();
    const seededModel =
      loadedModel.graphs.length > 0 ? loadedModel : createBlankModel();
    return applyServerRelationships(seededModel, relationships) ?? seededModel;
  }, []);

  const handleLoadModel = useCallback(async () => {
    setIsLoadingModel(true);
    setLoadError(null);
    try {
      const hydratedModel = await loadHydratedDataMartModel();
      setModel(hydratedModel);
      setSelectedGraphKey(hydratedModel.graphs[0]?.dataMart.data_mart ?? null);
      setSelectedEntityId(null);
      setSelectedRelationshipEdgeId(null);
    } catch (error) {
      console.error("Unable to load Data Mart model", error);
      const blankModel = createBlankModel();
      setModel(blankModel);
      setSelectedGraphKey(blankModel.graphs[0]?.dataMart.data_mart ?? null);
      setSelectedEntityId(null);
      setSelectedRelationshipEdgeId(null);
      setLoadError("Unable to load Data Mart metadata. Showing empty workspace.");
    } finally {
      setIsLoadingModel(false);
    }
  }, [loadHydratedDataMartModel]);

  useEffect(() => {
    handleLoadModel();
  }, [handleLoadModel]);

  useEffect(() => {
    return () => {
      if (generationSqlCopyFeedbackTimeoutRef.current) {
        window.clearTimeout(generationSqlCopyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const loadRelatedRdvEntities = useCallback(async () => {
    setIsLoadingRdvEntities(true);
    setRdvEntitiesError(null);
    try {
      const records = await fetchRelatedRdvEntities();
      setRdvEntities(records);
    } catch (error) {
      console.error("Unable to load related RDV entities", error);
      setRdvEntities([]);
      setRdvEntitiesError("Unable to load RDV entities.");
    } finally {
      setIsLoadingRdvEntities(false);
    }
  }, []);

  useEffect(() => {
    loadRelatedRdvEntities();
  }, [loadRelatedRdvEntities]);

  const loadAttachableGoldAssets = useCallback(async () => {
    setIsLoadingAttachableGoldAssets(true);
    setPromoteGoldAssetsError(null);
    try {
      const response = await getDataSources();
      const records = Array.isArray(response) ? response : [];
      const options = (records as DataSourceListRecord[])
        .filter((dataSource) => isInternalRegisteredSource(dataSource.data_source_type))
        .flatMap((dataSource) =>
          (Array.isArray(dataSource.source_entity) ? dataSource.source_entity : [])
            .filter((entity) => isGoldLayerEntity(entity.source_entity_layer))
            .map((entity) => ({
              data_source_id: dataSource.data_source_id,
              data_source_name: dataSource.data_source_name,
              source_entity_id: entity.source_entity_id,
              source_entity_name: entity.source_entity_name,
              source_entity_description: entity.source_entity_description ?? null,
              source_schema: entity.source_schema ?? null,
              source_entity_kind: entity.source_entity_kind ?? null,
              source_entity_origin: entity.source_entity_origin ?? null
            }))
        )
        .sort((left, right) => left.source_entity_name.localeCompare(right.source_entity_name));
      setAttachableGoldAssets(options);
    } catch (error) {
      console.error("Unable to load attachable internal Gold assets", error);
      setAttachableGoldAssets([]);
      setPromoteGoldAssetsError("Unable to load custom data mart entities.");
    } finally {
      setIsLoadingAttachableGoldAssets(false);
    }
  }, []);

  useEffect(() => {
    loadAttachableGoldAssets();
  }, [loadAttachableGoldAssets]);

  const handleExportModel = useCallback(() => {
    if (!model) {
      return;
    }
    const payload = {
      model: {
        graphs: model.graphs
      },
      metadata: {
        exported: new Date().toISOString(),
        version: "1.0"
      }
    };
    downloadFile(
      `data-mart-model-${Date.now()}.json`,
      JSON.stringify(payload, null, 2),
      "application/json"
    );
  }, [model]);

  const persistedLayout = useMemo(() => {
    if (!model) {
      return new Map<string, { x: number; y: number }>();
    }
    const next = new Map<string, { x: number; y: number }>();
    model.graphs.forEach((graph) => {
      [...graph.facts, ...graph.dimensions].forEach((entity) => {
        if (typeof entity.record.canvas_x === "number" && typeof entity.record.canvas_y === "number") {
          next.set(entity.id, {
            x: entity.record.canvas_x,
            y: entity.record.canvas_y
          });
        }
      });
    });
    return next;
  }, [model]);

  const currentGraph: DataMartGraph | null = useMemo(() => {
    if (!model) {
      return null;
    }
    if (!selectedGraphKey) {
      return model.graphs[0] ?? null;
    }
    const normalizedKey = selectedGraphKey.toLowerCase();
    return (
      model.graphs.find(
        (graph) => graph.dataMart.data_mart.toLowerCase() === normalizedKey
      ) ?? model.graphs[0] ?? null
    );
  }, [model, selectedGraphKey]);

  const hasUnsavedLayoutChanges = useMemo(
    () =>
      nodes.some((node) => {
        if (!isModeledDataMartNodeData(node.data)) {
          return false;
        }
        const targetEntityDmId = node.data.entity.record.target_entity_dm_id;
        if (!targetEntityDmId) {
          return false;
        }
        return hasCoordinateChanged(node.position, persistedLayout.get(node.id));
      }),
    [nodes, persistedLayout]
  );

  // Warn before leaving with unsaved canvas arrangement (layout persists only on Save Layout).
  useUnsavedChangesGuard(hasUnsavedLayoutChanges);

  const reserveCreatedNodePosition = useCallback(() => {
    const instance = reactFlowInstanceRef.current;
    if (!createdNodeClusterAnchorRef.current) {
      const viewportCenter = instance?.screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      });
      createdNodeClusterAnchorRef.current = viewportCenter ?? { x: 0, y: 0 };
      createdNodeClusterIndexRef.current = 0;
    }

    const anchor = createdNodeClusterAnchorRef.current;
    const index = createdNodeClusterIndexRef.current;
    const offset = NEW_ENTITY_CLUSTER_OFFSETS[index % NEW_ENTITY_CLUSTER_OFFSETS.length];
    const row = Math.floor(index / NEW_ENTITY_CLUSTER_OFFSETS.length);
    createdNodeClusterIndexRef.current = index + 1;
    return {
      x: anchor.x + offset.x,
      y: anchor.y + offset.y + row * NEW_ENTITY_CLUSTER_GAP_Y
    };
  }, []);

  const handleSaveLayout = useCallback(async () => {
    const modeledPayload = nodes
      .map((node) => {
        if (!isModeledDataMartNodeData(node.data)) {
          return null;
        }
        const targetEntityDmId = node.data.entity.record.target_entity_dm_id;
        if (!targetEntityDmId) {
          return null;
        }
        return {
          target_entity_dm_id: targetEntityDmId,
          canvas_x: node.position.x,
          canvas_y: node.position.y
        };
      })
      .filter((value): value is { target_entity_dm_id: number; canvas_x: number; canvas_y: number } => Boolean(value));

    if (!modeledPayload.length) {
      setStatusMessage("No layout changes to save.");
      return;
    }

    try {
      const updatedRecords = await updateDataMartLayout(modeledPayload);
      setModel((previous) => {
        if (!previous || !updatedRecords.length) {
          return previous;
        }
        const recordById = new Map(
          updatedRecords
            .filter((record) => record.target_entity_dm_id != null)
            .map((record) => [record.target_entity_dm_id as number, record])
        );

        return {
          ...previous,
          graphs: previous.graphs.map((graph) => ({
            ...graph,
            facts: graph.facts.map((fact) => {
              const targetId = fact.record.target_entity_dm_id;
              const nextRecord = targetId ? recordById.get(targetId) : null;
              return nextRecord ? { ...fact, record: { ...fact.record, ...nextRecord } } : fact;
            }),
            dimensions: graph.dimensions.map((dimension) => {
              const targetId = dimension.record.target_entity_dm_id;
              const nextRecord = targetId ? recordById.get(targetId) : null;
              return nextRecord
                ? { ...dimension, record: { ...dimension.record, ...nextRecord } }
                : dimension;
            })
          }))
        };
      });
      setStatusMessage("Layout saved.");
    } catch (error) {
      console.error("Unable to save Data Mart layout", error);
      setStatusMessage("Unable to save layout.");
    }
  }, [nodes]);

  const handleAutoArrangeLayout = useCallback(() => {
    if (!currentGraph) {
      return;
    }
    // Auto Arrange recomputes every position from scratch and there is no undo — so confirm before
    // it replaces a layout the user has changed (don't nag when nothing is dirty).
    if (
      hasUnsavedLayoutChanges &&
      !window.confirm(
        "Auto Arrange will replace the current arrangement and cannot be undone. Continue?"
      )
    ) {
      return;
    }
    // Star-schema default: facts on a spine, dimensions radiating around them (computeLayout).
    const layout = computeLayout(currentGraph);
    if (!layout.size) {
      setStatusMessage("No nodes to arrange.");
      return;
    }
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        const position = layout.get(node.id);
        return position ? { ...node, position } : node;
      })
    );
    setStatusMessage("Canvas arranged. Save layout to persist.");
    window.setTimeout(() => {
      void reactFlowInstanceRef.current?.fitView({ padding: 0.2, duration: 650, maxZoom: 1.15 });
    }, 50);
  }, [currentGraph, setNodes, hasUnsavedLayoutChanges]);

  // status auto-clear is handled by useStatusToast

  const loadDataMartOptions = useCallback(async () => {
    setIsLoadingDataMarts(true);
    setDataMartsError(null);
    try {
      const response = await getDataMarts();
      const records = Array.isArray(response)
        ? response
        : Array.isArray((response as { data_marts?: unknown[] })?.data_marts)
          ? ((response as { data_marts: unknown[] }).data_marts ?? [])
          : [];
      const normalized = records
        .map((record) => {
          const option = record as Partial<DataMartOption>;
          if (
            typeof option.data_mart !== "string" ||
            option.data_mart_id == null ||
            Number.isNaN(Number(option.data_mart_id))
          ) {
            return null;
          }
          return {
            data_mart: option.data_mart,
            data_mart_id: Number(option.data_mart_id)
          };
        })
        .filter((value): value is DataMartOption => Boolean(value));
      setDataMartOptions(normalized);
    } catch (error) {
      console.error("Unable to load Data Mart options", error);
      setDataMartOptions([]);
      setDataMartsError("Unable to load Data Mart list.");
    } finally {
      setIsLoadingDataMarts(false);
    }
  }, []);

  useEffect(() => {
    loadDataMartOptions();
  }, [loadDataMartOptions]);

  const handleCreateDataMartOption = useCallback(
    async (name: string): Promise<{ ok: boolean; option?: DataMartOption; error?: string }> => {
      const normalizedName = name.trim();
      if (!normalizedName) {
        return { ok: false, error: "Enter a data mart name." };
      }

      const existing = dataMartOptions.find(
        (option) => option.data_mart.trim().toLowerCase() === normalizedName.toLowerCase()
      );
      if (existing) {
        return { ok: true, option: existing };
      }

      try {
        const response = await createDataMart(normalizedName);
        const records = Array.isArray(response) ? response : [response];
        const record = records.find(
          (candidate) =>
            candidate &&
            typeof candidate.data_mart === "string" &&
            candidate.data_mart_id != null &&
            !Number.isNaN(Number(candidate.data_mart_id))
        );

        if (!record) {
          return { ok: false, error: "Unable to parse created Data Mart." };
        }

        const option = {
          data_mart: String(record.data_mart),
          data_mart_id: Number(record.data_mart_id)
        };

        setDataMartOptions((previous) =>
          [...previous, option]
            .filter(
              (candidate, index, array) =>
                array.findIndex(
                  (item) => item.data_mart.trim().toLowerCase() === candidate.data_mart.trim().toLowerCase()
                ) === index
            )
            .sort((a, b) => a.data_mart.localeCompare(b.data_mart))
        );

        return { ok: true, option };
      } catch (error) {
        console.error("Unable to create Data Mart", error);
        return {
          ok: false,
          error: error instanceof Error ? error.message : "Unable to create Data Mart."
        };
      }
    },
    [dataMartOptions]
  );

  const handleRun = useCallback(() => {
    const defaultDataMart =
      currentGraph?.dataMart.data_mart ||
      selectedGraphKey ||
      dataMartOptions[0]?.data_mart ||
      "";
    setGenerationDataMartName(defaultDataMart);
    setGenerationMode("model");
    setGenerationSqlPreview("");
    setGenerationSqlError(null);
    setGenerationSqlWarning(null);
    setGenerationCopyStatus("idle");
    setIsGenerationSqlStale(false);
    setGenerationModalOpen(true);
  }, [currentGraph, dataMartOptions, selectedGraphKey]);

  const handleGenerationDataMartChange = useCallback(
    (value: string) => {
      setGenerationDataMartName(value);
      setGenerationSqlError(null);
      setGenerationSqlWarning(null);
      setGenerationCopyStatus("idle");
      setIsGenerationSqlStale(Boolean(generationSqlPreview));
    },
    [generationSqlPreview]
  );

  const handleGenerationModeChange = useCallback(
    (mode: "model" | "dq") => {
      setGenerationMode((previous) => {
        if (previous === mode) {
          return previous;
        }
        // Switching modes invalidates the current preview — clear it so the user can't mistake a
        // model preview for a DQ preview (or vice versa).
        setGenerationSqlPreview("");
        setGenerationSqlError(null);
        setGenerationSqlWarning(null);
        setGenerationReport(null);
        setGenerationCopyStatus("idle");
        setIsGenerationSqlStale(false);
        return mode;
      });
    },
    []
  );

  const handleGenerateDataMartSqlPreview = useCallback(async () => {
    const dataMartName = generationDataMartName.trim();
    if (!dataMartName) {
      setGenerationSqlError("Select a data mart before generating SQL preview.");
      return;
    }
    setIsGenerationSqlLoading(true);
    setGenerationSqlError(null);
    setGenerationSqlWarning(null);
    setGenerationReport(null);
    setGenerationCopyStatus("idle");
    try {
      const response =
        generationMode === "dq"
          ? await generateDqTestDataMartSqlPreview(getEnvironmentId(), dataMartName)
          : await generateDataMartSqlPreview(getEnvironmentId(), dataMartName, null, {
              shouldGenerateInserts: generationGenerateInserts,
              fileSystem: isLakehouseTarget ? generationFileSystem : null
            });
      const sql = response.sql ?? "";
      setGenerationSqlPreview(sql);
      setGenerationReport(
        generationMode === "dq"
          ? null
          : ((response as DataMartSqlPreviewResponse).generation_report ?? null)
      );
      setIsGenerationSqlStale(false);
      if (!sql.trim()) {
        setGenerationSqlWarning(
          generationMode === "dq"
            ? "No DQ test SQL was generated. Check that this data mart has facts, dimensions, and column mappings."
            : "No SQL was generated. Check Data Mart facts, dimensions, mappings, and column mappings."
        );
      }
    } catch (error) {
      setGenerationSqlError(
        getApiErrorMessage(
          error,
          generationMode === "dq"
            ? "Unable to generate Data Mart DQ test preview."
            : "Unable to generate Data Mart SQL preview."
        )
      );
    } finally {
      setIsGenerationSqlLoading(false);
    }
  }, [
    generationDataMartName,
    generationMode,
    generationGenerateInserts,
    generationFileSystem,
    isLakehouseTarget
  ]);

  const handleCopyGenerationSqlPreview = useCallback(async () => {
    if (!generationSqlPreview || isGenerationSqlLoading) {
      return;
    }
    if (generationSqlCopyFeedbackTimeoutRef.current) {
      window.clearTimeout(generationSqlCopyFeedbackTimeoutRef.current);
      generationSqlCopyFeedbackTimeoutRef.current = undefined;
    }
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(generationSqlPreview);
      setGenerationCopyStatus("success");
    } catch (error) {
      console.error("Unable to copy Data Mart SQL preview", error);
      setGenerationCopyStatus("error");
    } finally {
      generationSqlCopyFeedbackTimeoutRef.current = window.setTimeout(() => {
        setGenerationCopyStatus("idle");
        generationSqlCopyFeedbackTimeoutRef.current = undefined;
      }, 2000);
    }
  }, [generationSqlPreview, isGenerationSqlLoading]);

  const handleCloseGenerationModal = useCallback(() => {
    setGenerationModalOpen(false);
  }, []);

  const persistRelationship = useCallback(
    async (
      fact: DataMartFact,
      dimension: DataMartDimension,
      handles: FactDimensionHandles,
      existingRecord?: ITargetEntityDmRelationshipRecord | null
    ) => {
      if (!fact.record.target_entity_dm_id || !dimension.record.target_entity_dm_id) {
        return;
      }
      try {
        const payload = {
          fact_target_entity_dm_id: fact.record.target_entity_dm_id,
          dimension_target_entity_dm_id: dimension.record.target_entity_dm_id,
          source_handle: handles.sourceHandle,
          target_handle: handles.targetHandle,
          relationship_status: "NEW" as const
        };
        if (existingRecord?.target_entity_dm_relationship_id) {
          await deleteTargetEntityRelationshipById(existingRecord.target_entity_dm_relationship_id);
        }
        const response = await createTargetEntityRelationships([payload]);
        const savedRecord = response?.[0];
        if (savedRecord) {
          setModel((previous) =>
            addRelationshipToModel(previous, fact.id, dimension.id, savedRecord, handles)
          );
        }
      } catch (error) {
        console.error("Unable to persist relationship", error);
        // The API explains WHY (e.g. a dimension without exactly one dimension key
        // column); a generic message leaves the user with nothing to act on.
        setLoadError(getApiErrorMessage(error, "Unable to persist relationship."));
        if (!existingRecord?.target_entity_dm_relationship_id) {
          setModel((previous) => removeRelationshipFromModel(previous, fact.id, dimension.id));
        } else {
          setModel((previous) =>
            addRelationshipToModel(
              previous,
              fact.id,
              dimension.id,
              existingRecord,
              handlesFromRelationshipRecord(existingRecord)
            )
          );
        }
      }
    },
    [setLoadError]
  );

  const appendEntity = useCallback(
    (kind: "fact" | "dimension") => {
      const position = reserveCreatedNodePosition();
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        const normalizedKey = selectedGraphKey?.toLowerCase();
        let createdEntityId: string | null = null;
        let entityAdded = false;

        const graphs = previous.graphs.map((graph, index) => {
          const matches = normalizedKey
            ? graph.dataMart.data_mart.toLowerCase() === normalizedKey
            : index === 0;
          if (!matches) {
            return graph;
          }
          entityAdded = true;
          if (kind === "fact") {
            const newFact = createFactEntity(graph);
            createdEntityId = newFact.id;
            return {
              ...graph,
              facts: [
                ...graph.facts,
                {
                  ...newFact,
                  record: {
                    ...newFact.record,
                    canvas_x: position.x,
                    canvas_y: position.y
                  }
                }
              ]
            };
          }
          const newDimension = createDimensionEntity(graph);
          createdEntityId = newDimension.id;
          return {
            ...graph,
            dimensions: [
              ...graph.dimensions,
              {
                ...newDimension,
                record: {
                  ...newDimension.record,
                  canvas_x: position.x,
                  canvas_y: position.y
                }
              }
            ]
          };
        });

        if (!entityAdded || !createdEntityId) {
          return previous;
        }
        pendingCreatedNodeFocusRef.current = createdEntityId;
        setActiveFilter(null);
        setSelectedEntityId(createdEntityId);
        setSelectedRelationshipEdgeId(null);
        return { ...previous, graphs };
      });
    },
    [reserveCreatedNodePosition, selectedGraphKey, setSelectedEntityId]
  );

  const handleCreateFact = useCallback(() => {
    appendEntity("fact");
  }, [appendEntity]);

  const handleCreateDimension = useCallback(() => {
    appendEntity("dimension");
  }, [appendEntity]);

  const handleSyncColumnsFromServer = useCallback(
    (entityId: string, columnRecords: ITargetEntityDmColumnRecord[]) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        let hasUpdated = false;
        const graphs = previous.graphs.map((graph) => {
          const factIndex = graph.facts.findIndex((fact) => fact.id === entityId);
          if (factIndex !== -1) {
            const fact = graph.facts[factIndex];
            const targetId = fact.record.target_entity_dm_id;
            const filteredRecords = targetId
              ? columnRecords.filter((record) => record.target_entity_dm_id === targetId)
              : [];
            const nextFacts = [...graph.facts];
            nextFacts[factIndex] = { ...fact, columns: filteredRecords.map(buildDataMartColumn) };
            hasUpdated = true;
            return { ...graph, facts: nextFacts };
          }
          const dimensionIndex = graph.dimensions.findIndex((dimension) => dimension.id === entityId);
          if (dimensionIndex !== -1) {
            const dimension = graph.dimensions[dimensionIndex];
            const targetId = dimension.record.target_entity_dm_id;
            const filteredRecords = targetId
              ? columnRecords.filter((record) => record.target_entity_dm_id === targetId)
              : [];
            const nextDimensions = [...graph.dimensions];
            nextDimensions[dimensionIndex] = {
              ...dimension,
              columns: filteredRecords.map(buildDataMartColumn)
            };
            hasUpdated = true;
            return { ...graph, dimensions: nextDimensions };
          }
          return graph;
        });
        if (!hasUpdated) {
          return previous;
        }
        return { ...previous, graphs };
      });
    },
    [setModel]
  );

  const handleUpdateEntityRecord = useCallback(
    (entityId: string, updates: Partial<ITargetEntityDmRecord>) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        const timestamp = new Date().toISOString();
        let hasUpdated = false;

        const graphs = previous.graphs.map((graph) => {
          const factIndex = graph.facts.findIndex((fact) => fact.id === entityId);
          if (factIndex !== -1) {
            const fact = graph.facts[factIndex];
            const nextFact: DataMartFact = {
              ...fact,
              record: { ...fact.record, ...updates, updated_at: timestamp }
            };
            const nextFacts = [...graph.facts];
            nextFacts[factIndex] = nextFact;
            hasUpdated = true;
            return { ...graph, facts: nextFacts };
          }
          const dimensionIndex = graph.dimensions.findIndex(
            (dimension) => dimension.id === entityId
          );
          if (dimensionIndex !== -1) {
            const dimension = graph.dimensions[dimensionIndex];
            const nextDimension: DataMartDimension = {
              ...dimension,
              record: { ...dimension.record, ...updates, updated_at: timestamp }
            };
            const nextDimensions = [...graph.dimensions];
            nextDimensions[dimensionIndex] = nextDimension;
            hasUpdated = true;
            return { ...graph, dimensions: nextDimensions };
          }
          return graph;
        });

        if (!hasUpdated) {
          return previous;
        }
        return { ...previous, graphs };
      });
    },
    [setModel]
  );

  const handleDeleteEntity = useCallback(
    async (entityId: string, entityType: "fact" | "dimension", targetEntityDmId?: number | null) => {
      try {
        if (targetEntityDmId != null) {
          await deleteTargetEntityDm(targetEntityDmId);
        }
        setModel((previous) => removeDataMartEntityFromModel(previous, entityId, entityType));
        setSelectedEntityId((previous) => (previous === entityId ? null : previous));
      } catch (error) {
        console.error("Unable to delete Data Mart entity", error);
        setLoadError(getApiErrorMessage(error, "Unable to delete Data Mart entity."));
        throw error;
      }
    },
    [setModel, setSelectedEntityId]
  );

  const handleManualConnect = useCallback(
    (connection: Connection) => {
      const resolved = resolveFactDimensionConnection(model, connection);
      let normalizedHandles: FactDimensionHandles | null = null;
      if (resolved) {
        const direction =
          connection.source === resolved.fact.id
            ? "fact-to-dimension"
            : "dimension-to-fact";
        normalizedHandles = normalizeFactDimensionHandles(connection, direction);
      }

      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        let hasUpdated = false;
        const graphs = previous.graphs.map((graph) => {
          const { graph: nextGraph, updated } = linkEntities(graph, connection);
          if (updated) {
            hasUpdated = true;
          }
          return nextGraph;
        });
        if (!hasUpdated) {
          return previous;
        }
        return { ...previous, graphs };
      });
      if (!resolved) {
        return;
      }
      if (!normalizedHandles) {
        return;
      }
      const existing = findRelationship(model, resolved.fact.id, resolved.dimension.id);
      if (
        resolved.fact.record.target_entity_dm_id &&
        resolved.dimension.record.target_entity_dm_id
      ) {
        const previousHandles: FactDimensionHandles = {
          sourceHandle: existing?.sourceHandle ?? null,
          targetHandle: existing?.targetHandle ?? null
        };
        const handleChanged =
          previousHandles.sourceHandle !== normalizedHandles.sourceHandle ||
          previousHandles.targetHandle !== normalizedHandles.targetHandle;

        if (!existing?.record?.target_entity_dm_relationship_id || handleChanged) {
          void persistRelationship(
            resolved.fact,
            resolved.dimension,
            normalizedHandles,
            existing?.record ?? null
          );
        }
      }
    },
    [model, persistRelationship]
  );

  const promotedGoldSourceEntityIds = useMemo(() => {
    if (!currentGraph) {
      return new Set<number>();
    }
    return new Set(
      [...currentGraph.facts, ...currentGraph.dimensions]
        .map((entity) => entity.record.source_entity_id)
        .filter((value): value is number => typeof value === "number")
    );
  }, [currentGraph]);

  const availablePromotableGoldAssets = useMemo(
    () =>
      attachableGoldAssets
        .filter((asset) => !promotedGoldSourceEntityIds.has(asset.source_entity_id))
        .sort((left, right) => left.source_entity_name.localeCompare(right.source_entity_name)),
    [attachableGoldAssets, promotedGoldSourceEntityIds]
  );

  const selectedPromotableGoldAsset = useMemo(
    () =>
      availablePromotableGoldAssets.find(
        (asset) => String(asset.source_entity_id) === selectedPromotableGoldAssetId
      ) ?? null,
    [availablePromotableGoldAssets, selectedPromotableGoldAssetId]
  );

  const selectedPromotionDataMart = useMemo(
    () =>
      dataMartOptions.find(
        (option) => option.data_mart.trim().toLowerCase() === selectedPromotionDataMartName.trim().toLowerCase()
      ) ?? null,
    [dataMartOptions, selectedPromotionDataMartName]
  );

  const handleOpenPromoteGoldModal = useCallback(() => {
    setSelectedPromotableGoldAssetId("");
    setSelectedPromotionDataMartName(currentGraph?.dataMart.data_mart ?? "");
    setIsAddingPromotionDataMart(false);
    setNewPromotionDataMartName("");
    setPromotionDataMartCreateError(null);
    setPromoteTableConstruct(DIMENSION_TABLE_CONSTRUCT_DEFAULT);
    setPromoteGoldAssetsError(null);
    setPromoteGoldModalOpen(true);
  }, [currentGraph]);

  const handleClosePromoteGoldModal = useCallback(() => {
    setPromoteGoldModalOpen(false);
    setSelectedPromotableGoldAssetId("");
    setSelectedPromotionDataMartName("");
    setIsAddingPromotionDataMart(false);
    setNewPromotionDataMartName("");
    setPromotionDataMartCreateError(null);
    setPromoteGoldAssetsError(null);
  }, []);

  const handleCreatePromotionDataMart = useCallback(async () => {
    setPromotionDataMartCreateError(null);
    setIsCreatingPromotionDataMart(true);
    const result = await handleCreateDataMartOption(newPromotionDataMartName);
    setIsCreatingPromotionDataMart(false);
    if (!result.ok || !result.option) {
      setPromotionDataMartCreateError(result.error ?? "Unable to create Data Mart.");
      return;
    }
    setSelectedPromotionDataMartName(result.option.data_mart);
    setIsAddingPromotionDataMart(false);
    setNewPromotionDataMartName("");
  }, [handleCreateDataMartOption, newPromotionDataMartName]);

  const handlePromoteGoldAsset = useCallback(async () => {
    const normalizedDataMartName = selectedPromotionDataMartName.trim();
    if (!normalizedDataMartName) {
      setPromoteGoldAssetsError("Select a data mart for the promoted asset.");
      return;
    }
    if (!selectedPromotableGoldAsset) {
      setPromoteGoldAssetsError("Select a custom data mart entity to promote.");
      return;
    }
    setIsPromotingGoldAsset(true);
    setPromoteGoldAssetsError(null);
    try {
      const promoted = await promoteInternalGoldAssetToDataMart({
        source_entity_id: selectedPromotableGoldAsset.source_entity_id,
        datamart: normalizedDataMartName,
        data_mart_id: selectedPromotionDataMart?.data_mart_id ?? null,
        table_construct: promoteTableConstruct
      });
      await handleLoadModel();
      setSelectedGraphKey(promoted.datamart ?? normalizedDataMartName);
      setStatusMessage("Custom data mart entity promoted.");
      handleClosePromoteGoldModal();
    } catch (error) {
      console.error("Unable to promote internal Gold asset", error);
      setPromoteGoldAssetsError("Unable to promote custom data mart entity.");
    } finally {
      setIsPromotingGoldAsset(false);
    }
  }, [
    handleClosePromoteGoldModal,
    handleLoadModel,
    promoteTableConstruct,
    selectedPromotionDataMart,
    selectedPromotionDataMartName,
    selectedPromotableGoldAsset
  ]);

  const entityNameSuggestions = useMemo(() => {
    if (!currentGraph) {
      return [];
    }
    const unique = new Set<string>();
    [...currentGraph.facts, ...currentGraph.dimensions].forEach((entity) => {
      const value = entity.record.target_entity_dm_name?.trim();
      if (value) {
        unique.add(value);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [currentGraph]);

  const entitySearchMatch = useMemo(() => {
    const normalizedQuery = subjectAreaSearchQuery.trim().toLowerCase();
    if (!currentGraph || !normalizedQuery) {
      return {
        matchedNodeIds: new Set<string>()
      };
    }
    const matchedNodeIds = new Set<string>();
    [...currentGraph.facts, ...currentGraph.dimensions].forEach((entity) => {
      const entityName = entity.record.target_entity_dm_name?.trim().toLowerCase() ?? "";
      if (entityName && entityName === normalizedQuery) {
        matchedNodeIds.add(entity.id);
      }
    });
    return { matchedNodeIds };
  }, [currentGraph, subjectAreaSearchQuery]);

  const highlightedSearchNodeIds = entitySearchMatch.matchedNodeIds;
  const dataMartNameById = useMemo(
    () =>
      new Map<number, string>(
        dataMartOptions.map((option) => [option.data_mart_id, option.data_mart])
      ),
    [dataMartOptions]
  );

  const resolveEntityFilterValue = useCallback(
    (entity: DataMartFact | DataMartDimension, field: Exclude<DataMartFilterField, "">) => {
      if (field === "target_entity_dm_name") {
        return entity.record.target_entity_dm_name ?? "";
      }
      if (field === "datamart") {
        const directValue = entity.record.datamart?.trim() ?? "";
        if (directValue) {
          return directValue;
        }
        const dataMartId = entity.record.data_mart_id;
        if (typeof dataMartId === "number") {
          return dataMartNameById.get(dataMartId) ?? "";
        }
        return "";
      }
      if (field === "target_schema") {
        return entity.record.target_schema?.trim() ?? "";
      }
      return "";
    },
    [dataMartNameById]
  );

  const getFilterFieldLabel = useCallback((field: Exclude<DataMartFilterField, "">) => {
    if (field === "datamart") {
      return "Data mart";
    }
    if (field === "target_schema") {
      return "Target schema";
    }
    return "Data mart entity";
  }, []);

  // Distinct target schemas across all DM entities, for the dropdown.
  const targetSchemaFilterOptions = useMemo<string[]>(() => {
    const values = new Set<string>();
    model?.graphs.forEach((graph) => {
      [...graph.facts, ...graph.dimensions].forEach((entity) => {
        const value = entity.record.target_schema?.trim();
        if (value) {
          values.add(value);
        }
      });
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [model]);

  // Distinct data-mart names for the filter dropdown (low cardinality — beats free text).
  const dataMartFilterOptions = useMemo<string[]>(() => {
    const names = new Set<string>();
    model?.graphs.forEach((graph) => {
      const name = graph.dataMart.data_mart?.trim();
      if (name) {
        names.add(name);
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [model]);

  const hasActiveFilter = Boolean(activeFilter?.field && activeFilter.value.trim());
  const activeFilterValueNormalized = activeFilter?.value.trim().toLowerCase() ?? "";

  const activeFilterMatchCount = useMemo(() => {
    if (!currentGraph || !hasActiveFilter || !activeFilter?.field) {
      return null;
    }
    return [...currentGraph.facts, ...currentGraph.dimensions].filter((entity) =>
      matchesCanvasFilter(
        resolveEntityFilterValue(entity, activeFilter.field),
        activeFilterValueNormalized,
        activeFilter.field
      )
    ).length;
  }, [
    activeFilter,
    activeFilterValueNormalized,
    currentGraph,
    hasActiveFilter,
    resolveEntityFilterValue
  ]);

  const handleFilterApply = useCallback(() => {
    if (!filterField) {
      setActiveFilter(null);
      setFilterPanelOpen(false);
      return;
    }
    const value = filterValueDraft.trim();
    if (!value) {
      setActiveFilter(null);
      setFilterPanelOpen(false);
      return;
    }
    setActiveFilter({ field: filterField, value });
    setFilterPanelOpen(false);
  }, [filterField, filterValueDraft]);

  const handleFilterClear = useCallback(() => {
    setFilterField("");
    setFilterValueDraft("");
    setActiveFilter(null);
    setFilterPanelOpen(false);
  }, []);

  const selectedRelationshipEdge = useMemo(
    () => edges.find((edge) => edge.id === selectedRelationshipEdgeId) ?? null,
    [edges, selectedRelationshipEdgeId]
  );

  const refreshHydratedModelPreservingSelection = useCallback(async () => {
    const previousGraphKey = selectedGraphKey;
    const previousEntityId = selectedEntityId;
    const hydratedModel = await loadHydratedDataMartModel();
    setModel(hydratedModel);
    setSelectedGraphKey(
      previousGraphKey && hydratedModel.graphs.some((graph) => graph.dataMart.data_mart === previousGraphKey)
        ? previousGraphKey
        : hydratedModel.graphs[0]?.dataMart.data_mart ?? null
    );
    if (
      previousEntityId &&
      hydratedModel.graphs.some((graph) =>
        [...graph.facts, ...graph.dimensions].some((entity) => entity.id === previousEntityId)
      )
    ) {
      setSelectedEntityId(previousEntityId);
    }
  }, [loadHydratedDataMartModel, selectedEntityId, selectedGraphKey]);

  const handleSelectEntity = useCallback((entityId: string) => {
    setSelectedEntityId(entityId);
    setSelectedRelationshipEdgeId(null);
  }, []);

  const handleRemoveRelationship = useCallback(
    async (
      factEntityId: string,
      dimensionEntityId: string,
      relationshipIdOverride?: number | null
    ) => {
      const existing = findRelationship(model, factEntityId, dimensionEntityId);
      const entities = findEntitiesByIds(model, factEntityId, dimensionEntityId);
      setModel((previous) => removeRelationshipFromModel(previous, factEntityId, dimensionEntityId));
      const factTargetId = entities?.fact.record.target_entity_dm_id ?? null;
      const dimensionTargetId = entities?.dimension.record.target_entity_dm_id ?? null;
      const relationshipId =
        relationshipIdOverride ?? existing?.record?.target_entity_dm_relationship_id ?? null;
      if (!relationshipId && (!factTargetId || !dimensionTargetId)) {
        return false;
      }
      try {
        if (relationshipId) {
          await deleteTargetEntityRelationshipById(relationshipId);
        } else if (factTargetId && dimensionTargetId) {
          await deleteTargetEntityRelationship(factTargetId, dimensionTargetId);
        }
        setSelectedRelationshipEdgeId(null);
        await refreshHydratedModelPreservingSelection();
        return true;
      } catch (error) {
        console.error("Unable to delete relationship", error);
        setLoadError(getApiErrorMessage(error, "Unable to delete relationship."));
        setModel((previous) =>
          addRelationshipToModel(previous, factEntityId, dimensionEntityId, existing?.record ?? null)
        );
        return false;
      }
    },
    [model, refreshHydratedModelPreservingSelection, setLoadError]
  );

  const handleDeleteRelationshipEdge = useCallback(
    async (edge: Edge<DataMartEdgeData> | null) => {
      if (!edge?.data) {
        setLoadError("Select a relationship line to delete.");
        return;
      }
      if (edge.data.relationshipType !== "fact-dimension") {
        setLoadError("Only Fact -> Dimension relationships can be deleted from the Data Mart canvas.");
        return;
      }
      setDeletingRelationshipEdgeId(edge.id);
      try {
        const deleted = await handleRemoveRelationship(
          edge.source,
          edge.target,
          edge.data.relationshipId
        );
        if (deleted) {
          setStatusMessage("Relationship deleted.");
        }
      } finally {
        setDeletingRelationshipEdgeId(null);
      }
    },
    [handleRemoveRelationship]
  );

  const handleEdgeClick = useCallback((event: React.MouseEvent, edge: Edge<DataMartEdgeData>) => {
    event.stopPropagation();
    setSelectedRelationshipEdgeId(edge.id);
    setSelectedEntityId(null);
  }, []);

  const handleEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge<DataMartEdgeData>) => {
      event.preventDefault();
      event.stopPropagation();
      setSelectedRelationshipEdgeId(edge.id);
    },
    []
  );

  useEffect(() => {
    if (!selectedRelationshipEdge) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Backspace" && event.key !== "Delete") {
        return;
      }
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName;
      if (
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      void handleDeleteRelationshipEdge(selectedRelationshipEdge);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleDeleteRelationshipEdge, selectedRelationshipEdge]);

  useEffect(() => {
    if (!currentGraph) {
      setNodes([]);
      setEdges([]);
      setSelectedRelationshipEdgeId(null);
      return;
    }
    const hasEntitySearch = subjectAreaSearchQuery.trim().length > 0;
    const highlightSet = highlightedSearchNodeIds;
    const activeField = activeFilter?.field;
    const hasNodeFilter = hasActiveFilter && Boolean(activeField);
    setNodes((previousNodes) => {
      const { nodes: nextNodes, edges: nextEdges } = buildFlowElements(
        currentGraph,
        previousNodes,
        handleSelectEntity
      );
      const visibleNodes = hasNodeFilter && activeField
        ? nextNodes.filter((node) =>
            // EXACT for data mart and target schema: both come from a dropdown of real values,
            // so a substring match let one schema pull in every schema that starts the same way.
            matchesCanvasFilter(
              resolveEntityFilterValue(node.data.entity, activeField),
              activeFilterValueNormalized,
              activeField
            )
          )
        : nextNodes;
      const visibleNodeIds = new Set(visibleNodes.map((node) => node.id));
      const visibleEdges = nextEdges.filter(
        (edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
      );
      const styledNodes = visibleNodes.map((node) => {
        const isHighlighted = highlightSet.has(node.id);
        const isSelected = node.id === selectedEntityId;
        if (!hasEntitySearch) {
          return {
            ...node,
            selected: isSelected,
            style: { ...(node.style ?? {}), opacity: 1, filter: "none" }
          };
        }
        return {
          ...node,
          selected: isSelected,
          style: {
            ...(node.style ?? {}),
            opacity: isHighlighted ? 1 : 0.22,
            filter: isHighlighted ? "drop-shadow(0 0 14px rgba(3,35,74,0.30))" : "none"
          }
        };
      });
      const styledEdges = visibleEdges.map((edge) => {
        const isHighlighted = highlightSet.has(edge.source) && highlightSet.has(edge.target);
        const isSelected = edge.id === selectedRelationshipEdgeId;
        if (!hasEntitySearch) {
          return {
            ...edge,
            selected: isSelected,
            animated: isSelected || Boolean(edge.animated),
            style: {
              ...(edge.style ?? {}),
              filter: isSelected ? "drop-shadow(0 0 8px rgba(15, 23, 42, 0.35))" : "none",
              opacity: 1,
              strokeWidth: isSelected ? 4 : edge.style?.strokeWidth
            }
          };
        }
        return {
          ...edge,
          selected: isSelected,
          animated: isSelected || Boolean(edge.animated),
          style: {
            ...(edge.style ?? {}),
            filter: isSelected ? "drop-shadow(0 0 8px rgba(15, 23, 42, 0.35))" : "none",
            opacity: isHighlighted || isSelected ? 0.95 : 0.1,
            strokeWidth: isSelected ? 4 : edge.style?.strokeWidth
          }
        };
      });
      setEdges(styledEdges);
      return styledNodes;
    });
  }, [
    currentGraph,
    handleSelectEntity,
    setNodes,
    setEdges,
    highlightedSearchNodeIds,
    subjectAreaSearchQuery,
    hasActiveFilter,
    activeFilter,
    activeFilterValueNormalized,
    resolveEntityFilterValue,
    selectedRelationshipEdgeId,
    selectedEntityId
  ]);

  useEffect(() => {
    if (!selectedRelationshipEdgeId) {
      return;
    }
    if (edges.some((edge) => edge.id === selectedRelationshipEdgeId)) {
      return;
    }
    setSelectedRelationshipEdgeId(null);
  }, [edges, selectedRelationshipEdgeId]);

  useEffect(() => {
    const normalizedQuery = subjectAreaSearchQuery.trim().toLowerCase();
    if (!normalizedQuery || highlightedSearchNodeIds.size === 0) {
      lastFocusedEntitySearchKeyRef.current = "";
      return;
    }
    const instance = reactFlowInstanceRef.current;
    if (!instance) {
      return;
    }
    const matchedNodeList = Array.from(highlightedSearchNodeIds).sort((a, b) => a.localeCompare(b));
    const focusKey = `${normalizedQuery}:${matchedNodeList.join("|")}`;
    if (focusKey === lastFocusedEntitySearchKeyRef.current) {
      return;
    }
    lastFocusedEntitySearchKeyRef.current = focusKey;
    // Jump to the FIRST match (zoomed in), matching the RDV canvas. Previously this fit the
    // bounding box of ALL matches, which barely moved / zoomed out when many nodes matched — so it
    // read as "search doesn't pan to the node".
    void instance.fitView({
      nodes: [{ id: matchedNodeList[0] }],
      duration: 600,
      padding: 0.35,
      maxZoom: 1.4
    });
  }, [highlightedSearchNodeIds, subjectAreaSearchQuery]);

  useEffect(() => {
    const nodeId = pendingCreatedNodeFocusRef.current;
    if (!nodeId || !nodes.some((node) => node.id === nodeId)) {
      return;
    }
    const instance = reactFlowInstanceRef.current;
    if (!instance) {
      return;
    }
    pendingCreatedNodeFocusRef.current = null;
    const focusCreatedNode = (remainingRetries: number) => {
      const node = instance.getNode(nodeId);
      const position = node?.positionAbsolute ?? node?.position;
      if (!position) {
        if (remainingRetries > 0) {
          window.requestAnimationFrame(() => focusCreatedNode(remainingRetries - 1));
        }
        return;
      }
      const width = node?.width ?? CREATED_NODE_FOCUS_WIDTH;
      const height = node?.height ?? CREATED_NODE_FOCUS_HEIGHT;
      void instance.setCenter(position.x + width / 2, position.y + height / 2, {
        duration: 650,
        zoom: CREATED_NODE_FOCUS_ZOOM
      });
    };
    window.requestAnimationFrame(() => focusCreatedNode(CREATED_NODE_FOCUS_RETRIES));
  }, [nodes]);

  const selectedEntity: SelectedDataMartEntity = useMemo(() => {
    if (!currentGraph || !selectedEntityId) {
      return null;
    }
    const fact = currentGraph.facts.find((candidate) => candidate.id === selectedEntityId);
    if (fact) {
      return { type: "fact", entity: fact };
    }
    const dimension = currentGraph.dimensions.find(
      (candidate) => candidate.id === selectedEntityId
    );
    if (dimension) {
      return { type: "dimension", entity: dimension };
    }
    return null;
  }, [currentGraph, selectedEntityId]);

  useEffect(() => {
    if (!selectedEntityId) {
      setEntityPanelOpen(false);
    }
  }, [selectedEntityId]);

  // const handleGraphChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  //   setSelectedGraphKey(event.target.value);
  //   setSelectedEntityId(null);
  // };

  const creationDisabled = isLoadingModel;
  const promoteGoldDisabled = creationDisabled || !currentGraph;

  const handleOpenEntityPanel = () => {
    if (!selectedEntityId || !selectedEntity) {
      return;
    }
    setEntityPanelOpen(true);
  };

  const handleNodeClick = useCallback((event: React.MouseEvent, node: { id: string }) => {
    if (event.shiftKey || event.metaKey || event.ctrlKey) {
      return;
    }
    setSelectedEntityId(node.id);
    setSelectedRelationshipEdgeId(null);
  }, []);

  const handleNodeDoubleClick = useCallback((_: React.MouseEvent, node: { id: string }) => {
    setSelectedEntityId(node.id);
    setSelectedRelationshipEdgeId(null);
    setEntityPanelOpen(true);
  }, []);

  const handlePaneClick = useCallback(() => {
    setSelectedRelationshipEdgeId(null);
  }, []);

  const handleCloseEntityPanel = () => {
    setEntityPanelOpen(false);
  };

  return (
    <>
      <section className="tw-flex tw-h-[90vh] tw-w-full tw-flex-col tw-gap-4 tw-rounded-[2.5rem] tw-bg-[#f4f7fd]">
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <ToolbarButton icon={FiPlus} label="Add Fact" onClick={handleCreateFact} disabled={creationDisabled} />
            <ToolbarButton
              icon={FiPlus}
              label="Add Dimension"
              onClick={handleCreateDimension}
              disabled={creationDisabled}
            />
            <ToolbarButton
              icon={FiPlus}
              label="Promote from Catalog"
              onClick={handleOpenPromoteGoldModal}
              disabled={promoteGoldDisabled}
            />
            <ToolbarButton icon={FiDownload} label="Export Model" onClick={handleExportModel} />
            <ToolbarButton
              icon={FiGrid}
              label="Auto Arrange"
              onClick={handleAutoArrangeLayout}
              disabled={!currentGraph}
            />
            <ToolbarButton
              icon={FiAlignJustify}
              label="Save Layout"
              onClick={handleSaveLayout}
              disabled={!hasUnsavedLayoutChanges}
            />
          </div>
          <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                onClick={() => {
                  if (activeFilter) {
                    setFilterField(activeFilter.field);
                    setFilterValueDraft(activeFilter.value);
                  }
                  setFilterPanelOpen(true);
                }}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-700 hover:tw-border-[#03234a] hover:tw-text-[#03234a]"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
                </svg>
                Filter
              </button>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <input
                list="datamart-entity-name-options"
                type="text"
                value={subjectAreaSearchQuery}
                onChange={(event) => setSubjectAreaSearchQuery(event.target.value)}
                placeholder="Search node by target_entity_dm_name…"
                className="tw-w-56 tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-200"
              />
              {subjectAreaSearchQuery ? (
                <button
                  type="button"
                  onClick={() => setSubjectAreaSearchQuery("")}
                  className="tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-border-slate-400"
                >
                  Clear
                </button>
              ) : null}
              <datalist id="datamart-entity-name-options">
                {entityNameSuggestions.map((value) => (
                  <option key={value} value={value} />
                ))}
              </datalist>
            </div>
            {/* <select
              className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-text-slate-700"
              value={selectedGraphKey ?? ""}
              onChange={handleGraphChange}
              disabled={!model?.graphs.length}
            >
              {model?.graphs.map((graph) => (
                <option key={graph.dataMart.data_mart} value={graph.dataMart.data_mart}>
                  {graph.dataMart.data_mart} ({graph.facts.length}F/{graph.dimensions.length}D)
                </option>
              ))}
            </select> */}
            <ToolbarButton icon={FiPlay} label="Run" onClick={handleRun} />
          </div>
        </div>
        {loadError && (
          <div className="tw-rounded-2xl tw-bg-rose-50 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-rose-600">
            {loadError}
          </div>
        )}
        {subjectAreaSearchQuery.trim() && highlightedSearchNodeIds.size === 0 && !isLoadingModel ? (
          <div className="tw-rounded-2xl tw-bg-amber-50 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-amber-700">
            No entity matched "{subjectAreaSearchQuery.trim()}".
          </div>
        ) : null}
        {hasActiveFilter && activeFilterMatchCount === 0 && !isLoadingModel ? (
          <div className="tw-rounded-2xl tw-bg-amber-50 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-amber-700">
            No nodes matched {getFilterFieldLabel(activeFilter.field)} "{activeFilter?.value}".
          </div>
        ) : null}
        <main className="tw-flex tw-w-full tw-flex-1 tw-min-h-0">
        <div className="tw-relative tw-flex tw-h-full tw-w-full tw-min-h-[24rem] tw-flex-1 tw-rounded-[2.25rem] tw-border tw-border-slate-100 tw-bg-white tw-p-4 tw-shadow-[0_30px_80px_-50px_rgba(15,23,42,0.55)]">
        <div className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-1 tw-rounded-[1.75rem] tw-bg-[#f6f8ff] tw-min-h-0">
          {(!currentGraph || isLoadingModel) && (
            <div className="tw-absolute tw-inset-0 tw-z-20 tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-[#f6f8ff]/90 tw-text-center tw-backdrop-blur-sm tw-pointer-events-auto">
              <span className="tw-mb-3 tw-inline-flex tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-4 tw-border-slate-200 tw-border-t-[#03234a]" />
              <p className="tw-text-base tw-font-semibold tw-text-slate-700">
                {isLoadingModel ? "Loading Data Mart metadata..." : "No Data Mart metadata available"}
              </p>
              <p className="tw-mt-1 tw-text-sm tw-text-slate-500">
                {isLoadingModel
                  ? "Please wait while we fetch the target entities."
                  : "Use the buttons above to create facts and dimensions."}
              </p>
            </div>
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onInit={(instance) => {
              reactFlowInstanceRef.current = instance;
            }}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            onNodeDoubleClick={handleNodeDoubleClick}
            onPaneClick={handlePaneClick}
            {...SHARED_CANVAS_INTERACTION_PROPS}
            onConnect={handleManualConnect}
            onEdgeClick={handleEdgeClick}
            onEdgeContextMenu={handleEdgeContextMenu}
            fitView
            fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
            nodeTypes={nodeTypes}
            className="tw-rounded-[1.75rem] tw-bg-[#f6f7fb]"
            style={{ width: "100%", height: "100%" }}
          >
            <MiniMap
              nodeColor={(node) => (node.type === "factNode" ? "#0ea5e9" : "#22c55e")}
            />
            <Controls />
            <Background gap={24} color="#e2e8f0" />
          </ReactFlow>
          {selectedEntity ? (
            <button
              type="button"
              onClick={handleOpenEntityPanel}
              disabled={entityPanelOpen}
              className="tw-absolute tw-top-6 tw-right-6 tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600 hover:tw-border-[#03234a] hover:tw-text-[#03234a] disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
            >
              View details
            </button>
          ) : null}
          {selectedRelationshipEdge ? (
            <div
              className={`tw-absolute tw-right-6 ${selectedEntity ? "tw-top-20" : "tw-top-6"} tw-z-10 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-rose-200 tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 tw-shadow`}
            >
              <span>
                {selectedRelationshipEdge.data?.relationshipType === "fact-dimension"
                  ? "Fact -> Dimension"
                  : "Dimension -> Dimension"}
              </span>
              <button
                type="button"
                onClick={() => void handleDeleteRelationshipEdge(selectedRelationshipEdge)}
                disabled={deletingRelationshipEdgeId === selectedRelationshipEdge.id}
                className="tw-rounded-full tw-bg-rose-600 tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-text-white hover:tw-bg-rose-700 disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300"
              >
                {deletingRelationshipEdgeId === selectedRelationshipEdge.id ? "Deleting" : "Delete"}
              </button>
            </div>
          ) : null}
          {statusMessage ? (
            <div className="tw-absolute tw-right-6 tw-top-32 tw-rounded-full tw-border tw-border-blue-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-text-blue-700 tw-shadow">
              {statusMessage}
            </div>
          ) : null}
        </div>
        </div>
      </main>
      </section>
      {generationModalOpen ? (
        <div className="tw-fixed tw-inset-0 tw-z-[1900] tw-flex tw-items-center tw-justify-center tw-bg-slate-900/40 tw-backdrop-blur-sm">
          <div className="tw-mx-4 tw-flex tw-max-h-[85vh] tw-w-full tw-max-w-6xl tw-flex-col tw-rounded-3xl tw-border tw-border-slate-200 tw-bg-white tw-p-6 tw-shadow-2xl">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div>
                <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">
                  Data Mart
                </p>
                <h2 className="tw-text-lg tw-font-semibold tw-text-slate-900">
                  Model Generation
                </h2>
              </div>
              <button
                type="button"
                onClick={handleCloseGenerationModal}
                className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-sm tw-text-slate-600 hover:tw-bg-slate-100"
              >
                Close
              </button>
            </div>
            <div className="tw-mt-4 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-space-y-4">
              <div>
                <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                  Data Mart
                </label>
                <select
                  value={generationDataMartName}
                  onChange={(event) => handleGenerationDataMartChange(event.target.value)}
                  disabled={isGenerationSqlLoading || isLoadingDataMarts}
                  className="tw-mt-1 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                >
                  <option value="" disabled>
                    Select data mart
                  </option>
                  {dataMartOptions.map((option) => (
                    <option key={option.data_mart_id} value={option.data_mart}>
                      {option.data_mart}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
                <div>

                  <div className="tw-mt-1 tw-inline-flex tw-rounded-full tw-border tw-border-slate-200 tw-bg-slate-100 tw-p-0.5">
                    {(
                      [
                        { value: "model", label: "Model SQL" },
                        { value: "dq", label: "DQ Tests" }
                      ] as const
                    ).map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleGenerationModeChange(option.value)}
                        disabled={isGenerationSqlLoading}
                        className={`tw-rounded-full tw-px-4 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-colors disabled:tw-cursor-not-allowed ${
                          generationMode === option.value
                            ? "tw-bg-white tw-text-[#03234a] tw-shadow-sm"
                            : "tw-text-slate-500 hover:tw-text-slate-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                {generationMode === "model" && isLakehouseTarget ? (
                  <div>
                    <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                      Inserts
                    </label>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={generationGenerateInserts}
                      onClick={() => {
                        setGenerationGenerateInserts((prev) => !prev);
                        setIsGenerationSqlStale(Boolean(generationSqlPreview));
                      }}
                      disabled={isGenerationSqlLoading}
                      title="Generate INSERT statements in addition to CREATE statements."
                      className={`tw-mt-1 tw-flex tw-h-[34px] tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-text-xs tw-font-semibold tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${
                        generationGenerateInserts
                          ? "tw-border-[#03234a] tw-bg-[#03234a] tw-text-white"
                          : "tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-border-slate-400"
                      }`}
                    >
                      {generationGenerateInserts ? "Inserts: On" : "Inserts: Off"}
                    </button>
                  </div>
                ) : null}
                {generationMode === "model" && isLakehouseTarget ? (
                  <div className="tw-min-w-[220px] tw-flex-1">
                    <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                      File system
                    </label>
                    <input
                      type="text"
                      value={generationFileSystem}
                      onChange={(event) => {
                        setGenerationFileSystem(event.target.value);
                        setIsGenerationSqlStale(Boolean(generationSqlPreview));
                      }}
                      disabled={isGenerationSqlLoading}
                      placeholder="Container name — generated SQL may be faulty if not supplied"
                      className="tw-mt-1 tw-h-[34px] tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300"
                    />
                  </div>
                ) : null}
              </div>
              <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-rounded-3xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <p className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                    {generationMode === "dq"
                      ? "Generated DQ test(s)"
                      : "Generated SQL"}
                  </p>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {isGenerationSqlStale ? (
                      <span className="tw-text-[11px] tw-font-medium tw-text-amber-600">
                        Stale
                      </span>
                    ) : null}
                    {generationCopyStatus === "success" ? (
                      <span className="tw-text-[11px] tw-font-medium tw-text-emerald-600">
                        Copied
                      </span>
                    ) : generationCopyStatus === "error" ? (
                      <span className="tw-text-[11px] tw-font-medium tw-text-rose-600">
                        Copy failed
                      </span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void handleGenerateDataMartSqlPreview()}
                      disabled={!generationDataMartName || isGenerationSqlLoading}
                      className="tw-rounded-full tw-border tw-border-[#03234a] tw-bg-[#03234a] tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white hover:tw-bg-[#021b39] focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-slate-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                    >
                      {isGenerationSqlLoading ? "Generating..." : "Generate"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCopyGenerationSqlPreview}
                      disabled={!generationSqlPreview || isGenerationSqlLoading}
                      className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[#03234a] hover:tw-border-slate-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-slate-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                {generationSqlError ? (
                  <p className="tw-mt-3 tw-rounded-2xl tw-bg-rose-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-rose-700">
                    {generationSqlError}
                  </p>
                ) : null}
                {generationSqlWarning ? (
                  <p className="tw-mt-3 tw-rounded-2xl tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-amber-700">
                    {generationSqlWarning}
                  </p>
                ) : null}
                <GenerationReportSummary report={generationReport} />
                <div className="tw-mt-2 tw-min-h-0 tw-w-full tw-min-w-0 tw-max-w-full tw-flex-1 tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                  {isGenerationSqlLoading ? (
                    <p className="tw-text-sm tw-text-slate-500">
                      {generationMode === "dq"
                        ? "Generating DQ test preview..."
                        : "Generating SQL preview..."}
                    </p>
                  ) : generationSqlPreview ? (
                    <SqlPreview value={generationSqlPreview} wrap showLineNumbers />
                  ) : (
                    <p className="tw-text-sm tw-text-slate-500">
                      {generationMode === "dq"
                        ? "Select a data mart, then generate DQ test preview."
                        : "Select a data mart, then generate SQL preview."}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div
        className={`tw-fixed tw-inset-0 tw-z-[1700] tw-transition-opacity tw-duration-300 tw-ease-in-out ${
          isFilterPanelOpen ? "tw-pointer-events-auto tw-opacity-100" : "tw-pointer-events-none tw-opacity-0"
        }`}
        aria-hidden={!isFilterPanelOpen}
      >
        <div
          className="tw-absolute tw-inset-0 tw-bg-slate-900/15 tw-backdrop-blur-sm"
          onClick={() => setFilterPanelOpen(false)}
        />
        <div
          className={`tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-w-full tw-max-w-full sm:tw-w-[24rem] tw-transform tw-transition-transform tw-duration-300 tw-ease-in-out ${
            isFilterPanelOpen ? "tw-translate-x-0" : "tw-translate-x-full"
          }`}
        >
          <div className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-border-l tw-border-slate-200 tw-bg-white tw-shadow-[0_32px_80px_-40px_rgba(15,23,42,0.75)]">
            <button
              type="button"
              onClick={() => setFilterPanelOpen(false)}
              className="tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600"
              aria-label="Close filter panel"
            >
              ×
            </button>

            <div className="tw-flex tw-h-full tw-flex-col tw-gap-5 tw-p-6 tw-pt-16">
              <div>
                <p className="tw-text-[0.7rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-blue-600">
                  Canvas Filter
                </p>
                <h3 className="tw-mt-2 tw-text-lg tw-font-semibold tw-text-slate-900">
                  Filter Data Mart Nodes
                </h3>
              </div>

              <label className="tw-flex tw-flex-col tw-gap-2">
                <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600">
                  Filter Field
                </span>
                <select
                  value={filterField}
                  onChange={(event) => {
                    const next = event.target.value as DataMartFilterField;
                    setFilterField(next);
                    if (!next) {
                      setFilterValueDraft("");
                    }
                  }}
                  className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                >
                  <option value="">Select filter field</option>
                  <option value="target_entity_dm_name">Data mart entity</option>
                  <option value="datamart">Data mart</option>
                  <option value="target_schema">Target schema</option>
                </select>
              </label>

              {filterField ? (
                <label className="tw-flex tw-flex-col tw-gap-2">
                  <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600">
                    Filter Value
                  </span>
                  {filterField === "datamart" ? (
                    <select
                      value={filterValueDraft}
                      onChange={(event) => setFilterValueDraft(event.target.value)}
                      className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                    >
                      <option value="">Select data mart…</option>
                      {dataMartFilterOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : filterField === "target_schema" ? (
                    <select
                      value={filterValueDraft}
                      onChange={(event) => setFilterValueDraft(event.target.value)}
                      className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                    >
                      <option value="">Select subject area…</option>
                      {targetSchemaFilterOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={filterValueDraft}
                      onChange={(event) => setFilterValueDraft(event.target.value)}
                      placeholder="Enter Data mart entity value"
                      className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                    />
                  )}
                </label>
              ) : null}

              <div className="tw-mt-auto tw-flex tw-gap-2">
                <button
                  type="button"
                  onClick={handleFilterClear}
                  className="tw-flex-1 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleFilterApply}
                  className="tw-flex-1 tw-rounded-lg tw-bg-[#03234a] tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#0a3f7f]"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`tw-fixed tw-inset-0 tw-z-[1800] tw-transition-opacity tw-duration-300 ${
          promoteGoldModalOpen ? "tw-pointer-events-auto tw-opacity-100" : "tw-pointer-events-none tw-opacity-0"
        }`}
        aria-hidden={!promoteGoldModalOpen}
      >
        <div
          className="tw-absolute tw-inset-0 tw-bg-slate-900/25 tw-backdrop-blur-sm"
          onClick={handleClosePromoteGoldModal}
        />
        <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw-w-[min(34rem,calc(100vw-2rem))] tw-max-w-full tw--translate-x-1/2 tw--translate-y-1/2 tw-rounded-[2rem] tw-border tw-border-slate-200 tw-bg-white tw-p-6 tw-shadow-[0_32px_90px_-40px_rgba(15,23,42,0.8)]">
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <div>
              <p className="tw-text-[0.7rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-amber-600">
                Internal Gold
              </p>
              <h3 className="tw-mt-2 tw-text-xl tw-font-semibold tw-text-slate-900">
                Promote custom entity to Data Mart
              </h3>
              <p className="tw-mt-2 tw-text-sm tw-text-slate-500">
                Promote a catalog-owned custom data mart entity into a real data mart entity.
              </p>
            </div>
            <button
              type="button"
              onClick={handleClosePromoteGoldModal}
              className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600"
              aria-label="Close promote internal gold asset modal"
            >
              ×
            </button>
          </div>
          <div className="tw-mt-6 tw-space-y-4">
            <label className="tw-flex tw-flex-col tw-gap-2">
              <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600">
                Data Mart
              </span>
              <select
                value={selectedPromotionDataMartName}
                onChange={(event) => setSelectedPromotionDataMartName(event.target.value)}
                disabled={isLoadingDataMarts || !dataMartOptions.length}
                className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-3 tw-text-sm tw-text-slate-800 focus:tw-border-amber-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-100"
              >
                <option value="">Select data mart</option>
                {dataMartOptions.map((option) => (
                  <option key={option.data_mart_id} value={option.data_mart}>
                    {option.data_mart}
                  </option>
                ))}
              </select>
            </label>
            <div className="tw--mt-1">
              {isAddingPromotionDataMart ? (
                <div className="tw-space-y-2 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3">
                  <input
                    type="text"
                    value={newPromotionDataMartName}
                    onChange={(event) => setNewPromotionDataMartName(event.target.value)}
                    placeholder="Enter new data mart name"
                    className="tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2.5 tw-text-sm tw-text-slate-800 focus:tw-border-amber-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-100"
                  />
                  <div className="tw-flex tw-justify-end tw-gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        await handleCreatePromotionDataMart();
                      }}
                      className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-[#03234a] tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                      disabled={isCreatingPromotionDataMart || !newPromotionDataMartName.trim()}
                    >
                      {isCreatingPromotionDataMart ? "Creating..." : "Create"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingPromotionDataMart(false);
                        setNewPromotionDataMartName("");
                        setPromotionDataMartCreateError(null);
                      }}
                      className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-border-slate-400 hover:tw-text-slate-800"
                      disabled={isCreatingPromotionDataMart}
                    >
                      Cancel
                    </button>
                  </div>
                  {promotionDataMartCreateError ? (
                    <p className="tw-text-xs tw-text-rose-600">{promotionDataMartCreateError}</p>
                  ) : null}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingPromotionDataMart(true);
                    setPromotionDataMartCreateError(null);
                  }}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-appearance-none tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-[13px] tw-font-semibold tw-text-[#03234a] tw-underline tw-decoration-[#03234a]/35 tw-underline-offset-2 hover:tw-text-[#021b39] hover:tw-decoration-[#021b39] focus:tw-outline-none"
                >
                  <span aria-hidden="true" className="tw-text-[15px] tw-leading-none">+</span>
                  Add new data mart
                </button>
              )}
            </div>
            <label className="tw-flex tw-flex-col tw-gap-2">
              <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600">
                Custom Data Mart Entity
              </span>
              <select
                value={selectedPromotableGoldAssetId}
                onChange={(event) => setSelectedPromotableGoldAssetId(event.target.value)}
                disabled={isLoadingAttachableGoldAssets || !availablePromotableGoldAssets.length}
                className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-3 tw-text-sm tw-text-slate-800 focus:tw-border-amber-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-100"
              >
                <option value="">Select custom data mart entity</option>
                {availablePromotableGoldAssets.map((asset) => (
                  <option key={asset.source_entity_id} value={asset.source_entity_id}>
                    {asset.source_entity_name} {asset.source_schema ? `(${asset.source_schema})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="tw-flex tw-flex-col tw-gap-2">
              <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600">
                Promotion type
              </span>
              <select
                value={promoteTableConstruct}
                onChange={(event) => setPromoteTableConstruct(event.target.value)}
                className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-3 tw-text-sm tw-text-slate-800 focus:tw-border-amber-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-100"
              >
                <option value={FACT_TABLE_CONSTRUCT_DEFAULT}>Fact</option>
                {DIMENSION_TABLE_CONSTRUCT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {promoteGoldAssetsError ? (
              <div className="tw-rounded-2xl tw-bg-rose-50 tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-text-rose-600">
                {promoteGoldAssetsError}
              </div>
            ) : null}
            {!isLoadingDataMarts && !dataMartOptions.length ? (
              <div className="tw-rounded-2xl tw-bg-slate-50 tw-px-4 tw-py-3 tw-text-sm tw-text-slate-600">
                No data marts are available for promotion.
              </div>
            ) : null}
            {!isLoadingAttachableGoldAssets && !availablePromotableGoldAssets.length ? (
              <div className="tw-rounded-2xl tw-bg-slate-50 tw-px-4 tw-py-3 tw-text-sm tw-text-slate-600">
                No custom data mart entities are available for promotion.
              </div>
            ) : null}
            {selectedPromotableGoldAsset ? (
              <div className="tw-rounded-2xl tw-border tw-border-amber-100 tw-bg-amber-50/70 tw-p-4">
                <p className="tw-text-sm tw-font-semibold tw-text-slate-900">
                  {selectedPromotableGoldAsset.source_entity_name}
                </p>
                <p className="tw-mt-1 tw-text-xs tw-uppercase tw-tracking-[0.18em] tw-text-amber-700">
                  {selectedPromotableGoldAsset.source_schema || "gold"} · {selectedPromotableGoldAsset.source_entity_kind || "asset"}
                </p>
                {selectedPromotableGoldAsset.source_entity_description ? (
                  <p className="tw-mt-2 tw-text-sm tw-text-slate-600">
                    {selectedPromotableGoldAsset.source_entity_description}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-2">
            <button
              type="button"
              onClick={handleClosePromoteGoldModal}
              className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePromoteGoldAsset}
              disabled={!selectedPromotableGoldAsset || !selectedPromotionDataMartName.trim() || isPromotingGoldAsset}
              className="tw-rounded-full tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
            >
              {isPromotingGoldAsset ? "Promoting..." : "Promote asset"}
            </button>
          </div>
        </div>
      </div>
      {selectedEntity ? (
        <>
          <div
            className={`tw-fixed tw-inset-x-0 tw-top-16 tw-bottom-0 tw-z-[1900] tw-bg-slate-900/30 tw-backdrop-blur-sm tw-transition-opacity tw-duration-300 ${
              entityPanelOpen ? "tw-opacity-100" : "tw-pointer-events-none tw-opacity-0"
            }`}
            onClick={handleCloseEntityPanel}
          />
          <div
            className={`tw-fixed tw-right-4 tw-top-[4.75rem] tw-bottom-4 tw-z-[2000] tw-flex tw-w-[min(34rem,calc(100vw-2rem))] tw-transform tw-transition-transform tw-duration-300 ${
              entityPanelOpen ? "tw-translate-x-0" : "tw-translate-x-full"
            }`}
          >
            <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-[2rem] tw-border tw-border-slate-200 tw-bg-white tw-shadow-[0_28px_60px_rgba(15,23,42,0.24)]">
              <DataMartEntityPanel
                selectedEntity={selectedEntity}
                currentGraph={currentGraph}
                onClose={handleCloseEntityPanel}
                onUpdateEntity={handleUpdateEntityRecord}
                onDeleteEntity={handleDeleteEntity}
                onRemoveRelationship={handleRemoveRelationship}
                onSyncColumns={handleSyncColumnsFromServer}
                relatedRdvEntities={rdvEntities}
                isLoadingRdvEntities={isLoadingRdvEntities}
                rdvEntitiesError={rdvEntitiesError}
                onReloadRdvEntities={loadRelatedRdvEntities}
                dataMartOptions={dataMartOptions}
                isLoadingDataMarts={isLoadingDataMarts}
                dataMartsError={dataMartsError}
                onReloadDataMarts={loadDataMartOptions}
                onCreateDataMart={handleCreateDataMartOption}
                activeDwTargetType={activeDwTargetType}
              />
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

export function DataMartWorkspace(): JSX.Element {
  return (
    <ReactFlowProvider>
      <DataMartWorkspaceInner />
    </ReactFlowProvider>
  );
}
